import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Table, Select, Input, Textarea, InputNumber, Segmented, Typography, Button, Modal, message, DatePicker } from '../../ui';
import { MinusCircle, Undo2, Search, Clock, Plus, TrendingUp } from 'lucide-react';
import { Dayjs } from 'dayjs';
import { request, api } from '../../api/client';
import { useDebounce } from '../../hooks/useDebounce';
import { Pill } from '../../components/TwDataTable';
import { CreditNoteDrawer } from '../../components/CreditNoteDrawer';
import { TodRunDrawer } from './TodRunDrawer';
import { disableFuture } from '../../lib/dateRange';
import { usePermissionChecker } from '../../store/auth';

const inr = (v: any) => `₹${Number(v ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const inr2 = (v: any) => `₹${Number(v ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const STATUS_PILL: Record<string, string> = { POSTED: 'green', DRAFT: 'gold', CANCELLED: 'red' };

// Indian financial year (Apr–Mar). Returns e.g. "2026-27" for a date in/after April 2026.
function fyOf(d: Date): string {
  const y = d.getFullYear();
  const start = d.getMonth() >= 3 ? y : y - 1; // month 3 = April (0-indexed)
  return `${start}-${String((start + 1) % 100).padStart(2, '0')}`;
}
const FY_OPTIONS = (() => {
  const now = new Date();
  const cur = fyOf(now);
  const startYr = Number(cur.slice(0, 4));
  return [0, 1, 2].map((back) => {
    const s = startYr - back;
    return `${s}-${String((s + 1) % 100).padStart(2, '0')}`;
  });
})();

/**
 * Credit-note REGISTER (Accounts / Admin) — every credit note the system has issued: sales-return credit notes
 * AND standalone turnover/volume BONUS credits (a financial incentive a retailer earns for their annual
 * business). Filterable by status / date / retailer; a row opens the canonical note drawer (over this page).
 * Bonuses are maker-checker: drafted here ("New turnover bonus"), then approved/reversed from the drawer.
 * Debit entries are only the compensating debits from a reversal — they stay visible + traceable but debit is
 * not a first-class issuable type.
 */
export function CreditNotesPage() {
  const hasPerm = usePermissionChecker();
  const qc = useQueryClient();
  const canCreateBonus = hasPerm('CREDIT_NOTE.BONUS.CREATE');

  const [type, setType] = useState<string>('ALL');
  const [status, setStatus] = useState<string | undefined>();
  const [range, setRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const search = useDebounce(searchInput, 400);
  const [noteId, setNoteId] = useState<string | null>(null);
  const [bonusOpen, setBonusOpen] = useState(false);
  const [todOpen, setTodOpen] = useState(false);

  const params = useMemo(() => {
    const p: Record<string, any> = {};
    if (type !== 'ALL') p.type = type;
    if (status) p.status = status;
    if (search.trim()) p.search = search.trim();
    if (range?.[0] && range?.[1]) { p.from = range[0].format('YYYY-MM-DD'); p.to = range[1].format('YYYY-MM-DD'); }
    return p;
  }, [type, status, search, range]);

  const { data, isLoading } = useQuery({
    queryKey: ['credit-notes-register', params],
    queryFn: () => request<any>({ url: '/credit-debit-notes', params }),
  });
  const rows = data?.rows ?? [];
  const s = data?.summary ?? { creditCount: 0, creditAmt: 0, debitCount: 0, debitAmt: 0, reversedCount: 0, pendingCount: 0, pendingAmt: 0 };

  const refetchRegister = () => qc.invalidateQueries({ queryKey: ['credit-notes-register'] });

  return (
    <div>
      <div className="mb-3 flex items-center justify-between gap-2">
        <Typography.Title level={4} style={{ margin: 0 }}>Credit Notes</Typography.Title>
        {canCreateBonus && (
          <div className="flex gap-2">
            <Button variant="default" icon={<TrendingUp size={16} />} onClick={() => setTodOpen(true)}>Run TOD</Button>
            <Button variant="primary" icon={<Plus size={16} />} onClick={() => setBonusOpen(true)}>New turnover bonus</Button>
          </div>
        )}
      </div>

      {/* Totals */}
      <div className="mb-3 grid grid-cols-2 gap-3 md:grid-cols-3">
        <Kpi title="Credit notes" value={inr(s.creditAmt)} sub={`${s.creditCount} posted · returns + bonuses`} accent="border-l-success-500" valueCls="text-success-600" icon={<MinusCircle size={16} />} />
        <Kpi title="Pending approval" value={inr(s.pendingAmt)} sub={`${s.pendingCount} draft bonus${s.pendingCount === 1 ? '' : 'es'}`} accent="border-l-warning-500" valueCls="text-warning-600" icon={<Clock size={16} />} />
        <Kpi title="Reversed" value={String(s.reversedCount)} sub="cancelled · incl. reversal debits" accent="border-l-danger-500" valueCls="text-danger-600" icon={<Undo2 size={16} />} />
      </div>

      {/* Filters */}
      <div className="mb-3 flex flex-wrap items-center gap-2 rounded-card border border-line bg-surface px-3 py-2.5">
        <Segmented
          value={type}
          onChange={(v) => setType(String(v))}
          options={[{ label: 'All', value: 'ALL' }, { label: 'Credit', value: 'CREDIT' }]}
        />
        <Select
          placeholder="Status" className="w-37.5" value={status} onChange={setStatus}
          options={[{ value: 'POSTED', label: 'Issued' }, { value: 'DRAFT', label: 'Pending' }, { value: 'CANCELLED', label: 'Reversed' }]}
        />
        <DatePicker.RangePicker value={range as any} onChange={(v) => setRange(v as any)} disabledDate={disableFuture} />
        <Input
          prefix={<Search size={16} />} placeholder="Note no…" className="w-50"
          value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
        />
      </div>

      <div className="overflow-hidden rounded-card border border-line bg-surface">
        <Table
          rowKey="id"
          size="small"
          loading={isLoading}
          dataSource={rows}
          pagination={{ pageSize: 25, hideOnSinglePage: true }}
          scroll={{ x: 'max-content' }}
          onRow={(r: any) => ({ onClick: () => setNoteId(String(r.id)), style: { cursor: 'pointer' } })}
          locale={{ emptyText: 'No credit notes match these filters' }}
          columns={[
            { title: 'Note No', dataIndex: 'noteNo', render: (v: string) => <span className="font-medium text-brand-700 dark:text-brand-400">{v}</span> },
            {
              title: 'Type', dataIndex: 'noteType', width: 130,
              render: (v: string, r: any) => (
                <span className="inline-flex items-center gap-1">
                  <Pill color={v === 'CREDIT' ? 'green' : 'gold'}>{v === 'CREDIT' ? 'Credit' : 'Debit'}</Pill>
                  {r.source === 'TURNOVER_BONUS' && <Pill color="blue">Bonus</Pill>}
                </span>
              ),
            },
            { title: 'Date', dataIndex: 'noteDate', width: 110, render: (v: any) => (v ? String(v).substring(0, 10) : '—') },
            { title: 'Retailer', dataIndex: 'retailerName', render: (v: string, r: any) => (v ? <span>{r.retailerCode ? <span className="text-subtle">{r.retailerCode} · </span> : ''}{v}</span> : `#${r.retailerId}`) },
            { title: 'Against invoice', dataIndex: 'invoiceNo', width: 150, render: (v: string, r: any) => v || (r.source === 'TURNOVER_BONUS' ? <span className="text-subtle">FY {r.financialYear ?? '—'}</span> : '—') },
            { title: 'Return', dataIndex: 'returnNo', width: 130, render: (v: string) => v || '—' },
            { title: 'Amount', dataIndex: 'amount', align: 'right' as const, width: 120, render: (v: any, r: any) => <span className={r.status === 'CANCELLED' ? 'text-subtle line-through' : 'font-semibold text-content'}>{inr2(v)}</span> },
            { title: 'Status', dataIndex: 'status', width: 100, render: (v: string) => <Pill color={STATUS_PILL[v] ?? 'default'}>{v === 'CANCELLED' ? 'Reversed' : v === 'POSTED' ? 'Issued' : v === 'DRAFT' ? 'Pending' : v}</Pill> },
          ]}
        />
      </div>
      {data?.truncated && (
        <div className="mt-2 text-xs text-subtle">Showing the 500 most recent notes — the totals above still cover every note. Use the status / date / note-no. filters to find older ones.</div>
      )}

      <CreditNoteDrawer id={noteId} onClose={() => setNoteId(null)} onChanged={refetchRegister} />
      <BonusModal open={bonusOpen} onClose={() => setBonusOpen(false)} onCreated={() => { setBonusOpen(false); refetchRegister(); }} />
      <TodRunDrawer open={todOpen} onClose={() => setTodOpen(false)} onGenerated={() => { setTodOpen(false); refetchRegister(); }} />
    </div>
  );
}

// ── "New turnover bonus" — drafts a standalone financial incentive credit (awaits Admin/SA approval) ──
function BonusModal({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: () => void }) {
  const [retailerId, setRetailerId] = useState<string | undefined>();
  const [amount, setAmount] = useState<number | null>(null);
  const [financialYear, setFinancialYear] = useState<string>(FY_OPTIONS[0]);
  const [reason, setReason] = useState('');

  const { data: retailerData } = useQuery({
    queryKey: ['retailers-for-bonus'],
    queryFn: () => request<any>({ url: '/retailers', params: { pageSize: 1000 } }),
    enabled: open,
  });
  // The /retailers list is a paginated envelope keyed `data` (matches RetailersListPage) — not `rows`.
  const retailers: any[] = retailerData?.data ?? retailerData?.rows ?? (Array.isArray(retailerData) ? retailerData : []);

  const reset = () => { setRetailerId(undefined); setAmount(null); setReason(''); setFinancialYear(FY_OPTIONS[0]); };
  const create = useMutation({
    mutationFn: () => api.post('/credit-notes/bonus', { retailerId, amount, financialYear, reason: reason.trim() || undefined }),
    onSuccess: () => {
      message.success('Turnover bonus drafted — an Admin can now approve & post it');
      reset();
      onCreated();
    },
    onError: (e: any) => message.error(e?.response?.data?.message || 'Could not draft the bonus'),
  });

  const valid = !!retailerId && !!amount && amount > 0 && /^\d{4}-\d{2}$/.test(financialYear);

  return (
    <Modal
      open={open}
      title="New turnover / volume bonus"
      onOpenChange={(o) => { if (!o) { reset(); onClose(); } }}
      footer={
        <>
          <Button variant="default" onClick={() => { reset(); onClose(); }}>Cancel</Button>
          <Button variant="primary" loading={create.isPending} disabled={!valid} onClick={() => create.mutate()}>Draft bonus</Button>
        </>
      }
    >
      <p className="mb-3 text-sm text-muted">
        A financial credit a retailer earns for their annual business. It carries <span className="font-medium">no GST</span> and
        posts to the retailer's ledger as advance-in-credit (offsets future dues) once an Admin approves it.
      </p>
      <div className="space-y-3">
        <Field label="Retailer">
          <Select
            placeholder="Select a retailer" className="w-full"
            value={retailerId} onChange={setRetailerId}
            options={retailers.map((r: any) => ({ value: String(r.id), label: `${r.code ? `${r.code} · ` : ''}${r.companyName ?? r.name ?? `#${r.id}`}` }))}
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Bonus amount (₹)">
            <InputNumber
              min={1} className="w-full" value={amount} onChange={(v) => setAmount(v as number | null)}
              formatter={(v) => (v == null || v === '' ? '' : `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ','))} parser={(v) => (v || '').replace(/,/g, '')}
            />
          </Field>
          <Field label="Financial year">
            <Select className="w-full" value={financialYear} onChange={setFinancialYear}
              options={FY_OPTIONS.map((fy) => ({ value: fy, label: fy }))} />
          </Field>
        </div>
        <Field label="Reason / note (optional)">
          <Textarea rows={2} placeholder="e.g. Annual turnover incentive for exceeding target" value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
      </div>
    </Modal>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="mb-1 text-xs font-medium text-muted">{label}</div>
      {children}
    </label>
  );
}

function Kpi({ title, value, sub, accent, valueCls, icon }: { title: string; value: string; sub?: string; accent: string; valueCls?: string; icon?: React.ReactNode }) {
  return (
    <div className={`rounded-card border border-line border-l-[3px] ${accent} bg-surface px-4 py-3`}>
      <div className="flex items-center gap-1.5 text-xs text-muted">{icon}{title}</div>
      <div className={`mt-0.5 text-2xl font-bold ${valueCls ?? 'text-content'}`}>{value}</div>
      {sub && <div className="text-xs text-subtle">{sub}</div>}
    </div>
  );
}
